import React from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { pillarById, humanDuration } from '../utils/format';
import { taskById } from '../runner/tasks';

export default function GameCard({ recommendation, completed }) {
  // Display metadata comes from the task registry — the same source the runner
  // uses. A game the runner can play always has a name here, and a game it
  // cannot play can never be launched from this card.
  const task = taskById(recommendation.id);
  const primary = pillarById(recommendation.targetPillar || task?.pillar);
  const accent = primary?.accent || 'var(--accent-primary)';

  // A served id the registry cannot render — a stale recommendation, or a game
  // removed server-side (e.g. the old rotation/task-switch training pair) —
  // must never render as a clickable card. Starting a session for one is a
  // 404; the fix is to never offer the tap, not to catch the failure after.
  //
  // Non-interactive state is signalled by structure (no Link, a lock glyph
  // instead of the play glyph) — never by dimming text. `--text-secondary`
  // at full opacity is the same color/contrast this app already treats as
  // "readable but muted" everywhere else; a bare taskId is never shown, since
  // there is no learner-facing name for a game this build cannot resolve.
  if (!task) {
    return (
      <div
        role="group"
        aria-label="Training game unavailable"
        style={{
          display: 'block',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-4)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 'var(--radius-control)',
              background: 'var(--glass-bg-medium)',
              color: 'var(--text-secondary)',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <Lock size={16} strokeWidth={1.5} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 'var(--text-panel-size)', fontWeight: 700, color: 'var(--text-primary)' }}>
              Not available yet
            </div>
            <div style={{ fontSize: 'var(--text-numeric-size)', color: 'var(--text-secondary)', marginTop: 'var(--space-1)' }}>
              Update the app to play this one.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const taskName = task.name;

  // Meta line: duration always renders; level only when the backend supplied
  // a difficulty. There is nothing in todayRoutine.games[] that distinguishes
  // an "adaptive" (previously trained) game from a "new" one — no boolean or
  // session-count field exists in the contract — so that third mockup segment
  // is omitted rather than guessed.
  const metaParts = [humanDuration(recommendation.durationSeconds)];
  if (recommendation.recommendedDifficulty != null) {
    metaParts.push(`Level ${recommendation.recommendedDifficulty}`);
  }

  return (
    <div
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 9 }}>
        <span
          aria-hidden="true"
          style={{
            width: 7,
            height: 7,
            borderRadius: 2,
            background: accent,
            flexShrink: 0,
            display: 'inline-block',
          }}
        />
        <span
          style={{
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: 'var(--text-secondary)',
          }}
        >
          {primary?.label || 'Cognition'}
        </span>
      </div>

      <h3
        style={{
          margin: '0 0 4px',
          fontSize: 15,
          fontWeight: 600,
          lineHeight: 1.3,
          letterSpacing: '-0.012em',
          color: 'var(--text-primary)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {taskName}
      </h3>

      <p
        style={{
          margin: '0 0 14px',
          fontSize: 12.5,
          color: 'var(--text-tertiary)',
        }}
      >
        {metaParts.join(' · ')}
      </p>

      {completed ? (
        // Completed is a training game, not an assessment — replaying it is
        // harmless and nothing in the API forbids it (completedIds just
        // records what ran today). So this stays a real link back to the
        // game: same accent-tinted "Completed" treatment the mockup shows,
        // but interactive rather than inert. The accessible name still
        // leads with the game name and trails with "completed" so it reads
        // naturally and keeps matching how completion is announced.
        <Link
          to={`/g-score/train/${encodeURIComponent(recommendation.id)}`}
          state={{ durationSeconds: recommendation.durationSeconds ?? null }}
          aria-label={`${taskName} — completed`}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 34,
            borderRadius: 'var(--radius-control)',
            background: 'var(--accent-light)',
            border: '1px solid var(--accent-primary)',
            color: 'var(--accent-text)',
            gap: 7,
            fontSize: 12.5,
            fontWeight: 600,
            textDecoration: 'none',
            transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--accent-hover)';
            e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--accent-primary)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              background: 'var(--accent-primary)',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <svg width="9" height="9" viewBox="0 0 16 16" fill="none">
              <path
                d="M4.8 8.2l2.1 2.1 4.3-4.4"
                stroke="var(--text-inverted)"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Completed
        </Link>
      ) : (
        <Link
          to={`/g-score/train/${encodeURIComponent(recommendation.id)}`}
          // Duration is backend-supplied per routine entry, so it travels with
          // the navigation rather than being re-derived on the destination page.
          state={{ durationSeconds: recommendation.durationSeconds ?? null }}
          aria-label={taskName}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 34,
            borderRadius: 'var(--radius-control)',
            background: 'var(--btn-secondary-bg)',
            border: '1px solid var(--btn-secondary-border)',
            color: 'var(--btn-secondary-text)',
            fontSize: 12.5,
            fontWeight: 600,
            textDecoration: 'none',
            transition: 'background var(--transition-fast), border-color var(--transition-fast)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--btn-secondary-hover)';
            e.currentTarget.style.borderColor = 'var(--border-strong)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--btn-secondary-bg)';
            e.currentTarget.style.borderColor = 'var(--btn-secondary-border)';
          }}
        >
          Start exercise
        </Link>
      )}
    </div>
  );
}
